import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    makeStyles,
    Select,
    Spinner,
    Tab,
    TabList,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular, EditRegular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import type { ProjectUserInfo } from '../../models/ProjectUserInfo';
import type { ProjektRisikoItem } from '../../models/ProjektRisikoItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingVerticalL,
        gap: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    tabContent: {
        flex: 1,
        overflow: 'auto',
        padding: tokens.spacingVerticalM,
    },
    toolbar: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    tableContainer: {
        overflowX: 'auto',
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    tableHeader: {
        backgroundColor: tokens.colorNeutralBackground2,
        padding: tokens.spacingVerticalS,
        fontSize: tokens.fontSizeBase300,
        fontWeight: '600',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        textAlign: 'left',
    },
    tableCell: {
        padding: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        maxWidth: '150px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tableCellMultiline: {
        padding: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        maxWidth: '200px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'visible',
    },
    tableCellSmall: {
        padding: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        maxWidth: '100px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'visible',
    },
    tableCellLarge: {
        padding: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        maxWidth: '350px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'visible',
    },
    measuresList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    measuresListItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    measuresText: {
        width: '250px',
        fontSize: tokens.fontSizeBase300,
        wordBreak: 'break-word',
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        width: '140px',
        whiteSpace: 'nowrap',
    },
    statusDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    statusText: {
        fontSize: tokens.fontSizeBase300,
        whiteSpace: 'nowrap',
    },
    tableActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingVerticalL,
    },
    formFullWidth: {
        gridColumn: '1 / -1',
    },
    formSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: tokens.spacingVerticalM,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: '600',
        marginBottom: tokens.spacingVerticalS,
    },
    measureGroup: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
    },
    legendContainer: {
        marginTop: tokens.spacingVerticalL,
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    legendTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: '600',
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    legendContent: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: tokens.spacingHorizontalXXL,
        width: 'fit-content',
        margin: '0 auto',
    },
    legendColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    legendColumnTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: '600',
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    legendList: {
        margin: '0',
        paddingLeft: tokens.spacingHorizontalM,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        listStyle: 'disc',
    },
    legendListItem: {
        marginBottom: tokens.spacingVerticalXS,
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
});

interface RisikoFormData {
    risikoArt: string;
    ursachen: string;
    auswirkungen: string;
    maßnahmen1: string;
    status1: number;
    maßnahmen2: string;
    status2: number;
    maßnahmen3: string;
    status3: number;
    anmerkungen: string;
    chancen: string;
    eW: number;
    sAF: number;
    sAi: number;
    beschreibung: string;
}

const RiskManagement: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const { project: projekt } = useProject();

    const [risikoItems, setRisikoItems] = useState<ProjektRisikoItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [userInfo, setUserInfo] = useState<ProjectUserInfo | null>(null);
    const [editingItem, setEditingItem] = useState<ProjektRisikoItem | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ProjektRisikoItem | null>(null);

    const [formData, setFormData] = useState<RisikoFormData>({
        risikoArt: '',
        ursachen: '',
        auswirkungen: '',
        maßnahmen1: '',
        status1: 0,
        maßnahmen2: '',
        status2: 0,
        maßnahmen3: '',
        status3: 0,
        anmerkungen: '',
        chancen: '',
        eW: 1,
        sAF: 1,
        sAi: 1,
        beschreibung: '',
    });

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    useEffect(() => {
        const fetchUserInfo = async () => {
            if (dataService) {
                try {
                    const info = await dataService.getUserInfo();
                    setUserInfo(info);
                } catch (error) {
                    console.error('Error fetching user info:', error);
                }
            }
        };
        fetchUserInfo();
    }, [dataService]);

    const fetchRisikoItems = React.useCallback(async () => {
        if (!dataService || !projekt?.projektID) return;

        try {
            setIsLoading(true);
            const items = await dataService.getProjektRisiko(projekt.projektID);
            console.log('Fetched risiko items:', items);
            setRisikoItems(items || []);
        } catch (error) {
            console.error('Error fetching risiko items:', error);
        } finally {
            setIsLoading(false);
        }
    }, [dataService, projekt?.projektID]);

    useEffect(() => {
        fetchRisikoItems();
    }, [fetchRisikoItems]);

    const handleAddClick = () => {
        setEditingItem(null);
        setFormData({
            risikoArt: '',
            ursachen: '',
            auswirkungen: '',
            maßnahmen1: '',
            status1: 0,
            maßnahmen2: '',
            status2: 0,
            maßnahmen3: '',
            status3: 0,
            anmerkungen: '',
            chancen: '',
            eW: 1,
            sAF: 1,
            sAi: 1,
            beschreibung: '',
        });
        setIsDrawerOpen(true);
    };

    const handleEditClick = async (item: ProjektRisikoItem) => {
        if (!dataService) return;
        try {
            const fullItem = await dataService.getProjektRisikoItem(item.id);
            setEditingItem(fullItem);
            setFormData({
                risikoArt: fullItem.risikoArt || '',
                ursachen: fullItem.ursachen || '',
                auswirkungen: fullItem.auswirkungen || '',
                maßnahmen1: fullItem.maßnahmen1 || '',
                status1: fullItem.status1 || 0,
                maßnahmen2: fullItem.maßnahmen2 || '',
                status2: fullItem.status2 || 0,
                maßnahmen3: fullItem.maßnahmen3 || '',
                status3: fullItem.status3 || 0,
                anmerkungen: fullItem.anmerkung || '',
                chancen: fullItem.chancen || '',
                eW: fullItem.ew || 1,
                sAF: fullItem.sAf || 1,
                sAi: fullItem.sAi || 1,
                beschreibung: fullItem.beschreibung || '',
            });
            setIsDrawerOpen(true);
        } catch (error) {
            console.error('Error fetching risiko item:', error);
        }
    };

    const handleDeleteClick = (item: ProjektRisikoItem) => {
        setItemToDelete(item);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;
        try {
            await dataService.deleteProjektRisiko(itemToDelete.projektID, itemToDelete.risikoID);
            setDeleteConfirmOpen(false);
            setItemToDelete(null);
            await fetchRisikoItems();
        } catch (error) {
            console.error('Error deleting risiko item:', error);
        }
    };

    const handleSave = async () => {
        if (!dataService || !projekt?.projektID) return;

        try {
            const itemData = {
                projektId: projekt.projektID,
                risikoArt: formData.risikoArt,
                bewerter: editingItem?.bewerter || userInfo?.name || '',
                ursachen: formData.ursachen,
                auswirkungen: formData.auswirkungen,
                maßnahmen1: formData.maßnahmen1,
                status1: formData.status1,
                maßnahmen2: formData.maßnahmen2,
                status2: formData.status2,
                maßnahmen3: formData.maßnahmen3,
                status3: formData.status3,
                anmerkung: formData.anmerkungen,
                chancen: formData.chancen,
                eW: formData.eW,
                sAf: formData.sAF,
                sAi: formData.sAi,
                beschreibung: formData.beschreibung,
            };

            if (editingItem?.risikoID) {
                await dataService.updateProjektRisiko(editingItem.risikoID, itemData as unknown as ProjektRisikoItem);
            } else {
                await dataService.addProjektRisiko(itemData as unknown as ProjektRisikoItem);
            }

            handleDrawerClose();
            await fetchRisikoItems();
        } catch (error) {
            console.error('Error saving risiko item:', error);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setEditingItem(null);
        setFormData({
            risikoArt: '',
            ursachen: '',
            auswirkungen: '',
            maßnahmen1: '',
            status1: 0,
            maßnahmen2: '',
            status2: 0,
            maßnahmen3: '',
            status3: 0,
            anmerkungen: '',
            chancen: '',
            eW: 1,
            sAF: 1,
            sAi: 1,
            beschreibung: '',
        });
    };

    const statusOptions = [
        { label: 'Choose Status', value: 0 },
        { label: 'Umgesetzt', value: 1 },
        { label: 'In Ausarbeitung', value: 2 },
        { label: 'Neu', value: 3 },
    ];

    const scaleOptions = [1, 2, 3, 4, 5];

    const getStatusColor = (status: number): string => {
        switch (status) {
            case 1: // Umgesetzt
                return tokens.colorPaletteGreenBackground3;
            case 2: // In Ausarbeitung
                return tokens.colorPaletteYellowBackground3;
            case 3: // Neu
                return tokens.colorPaletteRedBackground3;
            default:
                return tokens.colorNeutralBackground3;
        }
    };

    const getStatusText = (status: number): string => {
        const option = statusOptions.find(opt => opt.value === status);
        return option ? option.label : '';
    };

    return (
        <div className={styles.container}>
            <TabList defaultSelectedValue="riskmanagement">
                <Tab value="riskmanagement">Risk Management</Tab>
            </TabList>

            <div className={styles.tabContent}>
                {isLoading ? (
                    <div className={styles.container}>
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading Risk Management..." labelPosition="below" />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.toolbar}>
                            <Button icon={<AddRegular />} appearance="primary" onClick={handleAddClick}>
                                Add Risk
                            </Button>
                        </div>

                        {risikoItems.length === 0 ? (
                            <div className={styles.emptyState}>No risks found</div>
                        ) : (
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={styles.tableHeader}>Actions</th>
                                            <th className={styles.tableHeader}>Risk ID</th>
                                            <th className={styles.tableHeader}>Added by</th>
                                            <th className={styles.tableHeader}>Risk</th>
                                            <th className={styles.tableHeader}>Potential Cause</th>
                                            <th className={styles.tableHeader}>Potential Impact</th>
                                            <th className={styles.tableHeader}>Measures for Risk Reduction</th>
                                            <th className={styles.tableHeader}>Comment</th>
                                            <th className={styles.tableHeader}>Chances</th>
                                            <th className={styles.tableHeader}>EW</th>
                                            <th className={styles.tableHeader}>SAf</th>
                                            <th className={styles.tableHeader}>RBf</th>
                                            <th className={styles.tableHeader}>SAI</th>
                                            <th className={styles.tableHeader}>RBI</th>
                                            <th className={styles.tableHeader}>Description</th>
                                            <th className={styles.tableHeader}>Review Demanded</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {risikoItems.map((item) => (
                                            <tr key={item.risikoID}>
                                                <td className={styles.tableCell}>
                                                    <div className={styles.tableActions}>
                                                        <Button
                                                            icon={<EditRegular />}
                                                            appearance="subtle"
                                                            onClick={() => handleEditClick(item)}
                                                            title="Edit"
                                                        />
                                                        <Button
                                                            icon={<DeleteRegular />}
                                                            appearance="subtle"
                                                            onClick={() => handleDeleteClick(item)}
                                                            title="Delete"
                                                        />
                                                    </div>
                                                </td>
                                                <td className={styles.tableCell}>{item.risikoID}</td>
                                                <td className={styles.tableCellSmall}>{item.bewerter}</td>
                                                <td className={styles.tableCellMultiline}>{item.risikoArt}</td>
                                                <td className={styles.tableCellSmall}>{item.ursachen}</td>
                                                <td className={styles.tableCellSmall}>{item.auswirkungen}</td>
                                                <td className={styles.tableCellLarge}>
                                                    <div className={styles.measuresList}>
                                                        {item.maßnahmen1 && (
                                                            <div className={styles.measuresListItem}>
                                                                <span className={styles.measuresText}>{item.maßnahmen1}</span>
                                                                <div className={styles.statusContainer}>
                                                                    <div
                                                                        className={styles.statusDot}
                                                                        style={{ backgroundColor: getStatusColor(item.status1) }}
                                                                    />
                                                                    <span className={styles.statusText}>{getStatusText(item.status1)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.maßnahmen2 && (
                                                            <div className={styles.measuresListItem}>
                                                                <span className={styles.measuresText}>{item.maßnahmen2}</span>
                                                                <div className={styles.statusContainer}>
                                                                    <div
                                                                        className={styles.statusDot}
                                                                        style={{ backgroundColor: getStatusColor(item.status2) }}
                                                                    />
                                                                    <span className={styles.statusText}>{getStatusText(item.status2)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.maßnahmen3 && (
                                                            <div className={styles.measuresListItem}>
                                                                <span className={styles.measuresText}>{item.maßnahmen3}</span>
                                                                <div className={styles.statusContainer}>
                                                                    <div
                                                                        className={styles.statusDot}
                                                                        style={{ backgroundColor: getStatusColor(item.status3) }}
                                                                    />
                                                                    <span className={styles.statusText}>{getStatusText(item.status3)}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={styles.tableCellMultiline}>{item.anmerkung}</td>
                                                <td className={styles.tableCellMultiline}>{item.chancen}</td>
                                                <td className={styles.tableCell}>{item.ewAsText}</td>
                                                <td className={styles.tableCell}>{item.sAfAsText}</td>
                                                <td className={styles.tableCell}>{item.rBfAsText}</td>
                                                <td className={styles.tableCell}>{item.sAiAsText}</td>
                                                <td className={styles.tableCell}>{item.rBiAsText}</td>
                                                <td className={styles.tableCellMultiline}>{item.beschreibung}</td>
                                                <td className={styles.tableCell}>
                                                    {item.ueberpruefungNotwendig ? 'Ja' : 'Nein'}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* General Project Risk Row */}
                                        {risikoItems.length > 0 && (
                                            <tr style={{ backgroundColor: tokens.colorNeutralBackground2, fontWeight: 'bold' }}>
                                                <td className={styles.tableCell}></td>
                                                <td className={styles.tableCellSmall}></td>
                                                <td className={styles.tableCellMultiline}></td>
                                                <td className={styles.tableCellSmall}>General Projectrisk</td>
                                                <td className={styles.tableCellSmall}></td>
                                                <td className={styles.tableCellLarge}></td>
                                                <td className={styles.tableCellMultiline}></td>
                                                <td className={styles.tableCellMultiline}></td>
                                                <td className={styles.tableCellMultiline}></td>
                                                <td className={styles.tableCell}>
                                                    {risikoItems.length > 0
                                                        ? (Math.round((risikoItems.reduce((sum, item) => sum + (item.ew || 0), 0) / risikoItems.length) * 10) / 10).toFixed(1)
                                                        : '-'}
                                                </td>
                                                <td className={styles.tableCell}>
                                                    {risikoItems.length > 0
                                                        ? (Math.round((risikoItems.reduce((sum, item) => sum + (item.sAf || 0), 0) / risikoItems.length) * 10) / 10).toFixed(1)
                                                        : '-'}
                                                </td>
                                                <td className={styles.tableCell}>
                                                    {risikoItems.length > 0
                                                        ? (Math.round((risikoItems.reduce((sum, item) => sum + (item.rBf || 0), 0) / risikoItems.length) * 10) / 10).toFixed(1)
                                                        : '-'}
                                                </td>
                                                <td className={styles.tableCell}>
                                                    {risikoItems.length > 0
                                                        ? (Math.round((risikoItems.reduce((sum, item) => sum + (item.sAi || 0), 0) / risikoItems.length) * 10) / 10).toFixed(1)
                                                        : '-'}
                                                </td>
                                                <td className={styles.tableCell}>
                                                    {risikoItems.length > 0
                                                        ? (Math.round((risikoItems.reduce((sum, item) => sum + (item.rBi || 0), 0) / risikoItems.length) * 10) / 10).toFixed(1)
                                                        : '-'}
                                                </td>
                                                <td className={styles.tableCellMultiline}></td>
                                                <td className={styles.tableCell}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Legend Section */}
                        <div className={styles.legendContainer}>
                            <h3 className={styles.legendTitle} style={{ textAlign: "center" }}>Legend</h3>
                            <div className={styles.legendContent}>
                                {/* PO - Probability of Occurrence */}
                                <div className={styles.legendColumn}>
                                    <p className={styles.legendColumnTitle}>PO (Probability of Occurrence):</p>
                                    <ul className={styles.legendList}>
                                        <li className={styles.legendListItem}>1 = &lt; 5 %</li>
                                        <li className={styles.legendListItem}>2 = &lt; 10 %</li>
                                        <li className={styles.legendListItem}>3 = &lt; 20 %</li>
                                        <li className={styles.legendListItem}>4 = &lt; 30 %</li>
                                        <li className={styles.legendListItem}>5 = &gt; 30 %</li>
                                    </ul>
                                </div>

                                {/* FD - Financial Damage */}
                                <div className={styles.legendColumn}>
                                    <p className={styles.legendColumnTitle}>FD (Financial Damage):</p>
                                    <ul className={styles.legendList}>
                                        <li className={styles.legendListItem}>1 = up to 5 % of planned project costs</li>
                                        <li className={styles.legendListItem}>2 = up to 10 %</li>
                                        <li className={styles.legendListItem}>3 = up to 20 %</li>
                                        <li className={styles.legendListItem}>4 = up to 30 %</li>
                                        <li className={styles.legendListItem}>5 = more than 30 %</li>
                                    </ul>
                                </div>

                                {/* ID - Image Damage */}
                                <div className={styles.legendColumn}>
                                    <p className={styles.legendColumnTitle}>ID (Image Damage):</p>
                                    <ul className={styles.legendList}>
                                        <li className={styles.legendListItem}>1 = insignificant influence</li>
                                        <li className={styles.legendListItem}>2 = minor influence</li>
                                        <li className={styles.legendListItem}>3 = noticeable influence</li>
                                        <li className={styles.legendListItem}>4 = critical influence</li>
                                        <li className={styles.legendListItem}>5 = threatening influence</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <Drawer
                    open={isDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleDrawerClose()}
                    position="end"
                    size="medium"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>{editingItem ? 'Edit Risk' : 'Add New Risk'}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        <div className={styles.formGrid}>
                            {/* added by - Read Only */}
                            <Field label="Added By" className={styles.formFullWidth}>
                                <Input
                                    value={editingItem?.bewerter || userInfo?.name || ''}
                                    disabled
                                />
                            </Field>

                            {/* Risk Type */}
                            <Field label="Risk" required className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.risikoArt}
                                    onChange={(_, data) => setFormData({ ...formData, risikoArt: data.value })}
                                    placeholder="Enter risk type"
                                />
                            </Field>

                            {/* Causes */}
                            <Field label="Potential Cause" className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.ursachen}
                                    onChange={(_, data) => setFormData({ ...formData, ursachen: data.value })}
                                    placeholder="Enter potential causes"
                                />
                            </Field>

                            {/* Impacts */}
                            <Field label="Potential Impact" className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.auswirkungen}
                                    onChange={(_, data) => setFormData({ ...formData, auswirkungen: data.value })}
                                    placeholder="Enter potential impacts"
                                />
                            </Field>

                            {/* Measures Section */}
                            <div className={styles.formFullWidth}>
                                <div className={styles.formSection}>
                                    <div className={styles.sectionTitle}>Measures For Risk Reduction</div>

                                    {/* Measure 1 */}
                                    <div className={styles.measureGroup}>
                                        <Field label="Maßnahme 1">
                                            <Textarea
                                                value={formData.maßnahmen1}
                                                onChange={(_, data) => setFormData({ ...formData, maßnahmen1: data.value })}
                                                placeholder="Enter measure 1"
                                            />
                                        </Field>
                                        <Field label="Status">
                                            <Select
                                                value={formData.status1.toString()}
                                                onChange={(_, data) => setFormData({ ...formData, status1: parseInt(data.value) || 0 })}
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value.toString()} label={opt.label}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Field>
                                    </div>

                                    {/* Measure 2 */}
                                    <div className={styles.measureGroup}>
                                        <Field label="Maßnahme 2">
                                            <Textarea
                                                value={formData.maßnahmen2}
                                                onChange={(_, data) => setFormData({ ...formData, maßnahmen2: data.value })}
                                                placeholder="Enter measure 2"
                                            />
                                        </Field>
                                        <Field label="Status">
                                            <Select
                                                value={formData.status2.toString()}
                                                onChange={(_, data) => setFormData({ ...formData, status2: parseInt(data.value) || 0 })}
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value.toString()} label={opt.label}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Field>
                                    </div>

                                    {/* Measure 3 */}
                                    <div className={styles.measureGroup}>
                                        <Field label="Maßnahme 3">
                                            <Textarea
                                                value={formData.maßnahmen3}
                                                onChange={(_, data) => setFormData({ ...formData, maßnahmen3: data.value })}
                                                placeholder="Enter measure 3"
                                            />
                                        </Field>
                                        <Field label="Status">
                                            <Select
                                                value={formData.status3.toString()}
                                                onChange={(_, data) => setFormData({ ...formData, status3: parseInt(data.value) || 0 })}
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value.toString()} label={opt.label}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Field>
                                    </div>
                                </div>
                            </div>

                            {/* Comments */}
                            <Field label="Comment" className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.anmerkungen}
                                    onChange={(_, data) => setFormData({ ...formData, anmerkungen: data.value })}
                                    placeholder="Enter notes"
                                />
                            </Field>

                            {/* Chances */}
                            <Field label="Chances" className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.chancen}
                                    onChange={(_, data) => setFormData({ ...formData, chancen: data.value })}
                                    placeholder="Enter opportunities"
                                />
                            </Field>

                            {/* Risk Assessment Section */}
                            <div className={styles.formFullWidth}>
                                <div className={styles.formSection}>
                                    <div className={styles.sectionTitle}>Risk Assessment</div>

                                    <Field label="PO (Probability of Occurrence)">
                                        <Select
                                            value={formData.eW.toString()}
                                            onChange={(_, data) => setFormData({ ...formData, eW: parseInt(data.value) || 1 })}
                                        >
                                            {scaleOptions.map(opt => (
                                                <option key={opt} value={opt.toString()} label={opt.toString()}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>

                                    <Field label="FD (Financial Damage)">
                                        <Select
                                            value={formData.sAF.toString()}
                                            onChange={(_, data) => setFormData({ ...formData, sAF: parseInt(data.value) || 1 })}
                                        >
                                            {scaleOptions.map(opt => (
                                                <option key={opt} value={opt.toString()} label={opt.toString()}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>

                                    <Field label="ID (Image Damage)">
                                        <Select
                                            value={formData.sAi.toString()}
                                            onChange={(_, data) => setFormData({ ...formData, sAi: parseInt(data.value) || 1 })}
                                        >
                                            {scaleOptions.map(opt => (
                                                <option key={opt} value={opt.toString()} label={opt.toString()}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </Select>
                                    </Field>
                                </div>
                            </div>

                            {/* Description */}
                            <Field label="Description" className={styles.formFullWidth}>
                                <Textarea
                                    value={formData.beschreibung}
                                    onChange={(_, data) => setFormData({ ...formData, beschreibung: data.value })}
                                    placeholder="Enter description"
                                />
                            </Field>

                            {/* Save/Cancel Buttons */}
                            <div className={styles.formFullWidth} style={{ display: 'flex', gap: tokens.spacingHorizontalM, justifyContent: 'flex-end', marginTop: tokens.spacingVerticalL }}>
                                <Button onClick={() => handleDrawerClose()} appearance="secondary">
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} appearance="primary" disabled={!formData.risikoArt.trim()}>
                                    {editingItem ? 'Update' : 'Create'}
                                </Button>
                            </div>
                        </div>
                    </DrawerBody>
                </Drawer>

                <Dialog open={deleteConfirmOpen} onOpenChange={(_, data) => setDeleteConfirmOpen(data.open)}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Delete Risk</DialogTitle>
                            <DialogContent>
                                Are you sure you want to delete this risk item?
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={() => setDeleteConfirmOpen(false)}>
                                    Cancel
                                </Button>
                                <Button appearance="primary" onClick={handleConfirmDelete}>
                                    Delete
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </div>

        </div>
    );
};

export default RiskManagement;
